import './style.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  createSchoolGroup,
  getMunicipalities,
  getMeetingsByGroup,
  getOrCreateAdvisorPhaseMeeting,
  getSchools,
  getUsers
} from '../../API/apiService';
import ModalContainer from '../../components/modalContainer';
import CreateSchool from '../../components/adminSchools/createSchool';
import ProjectLinkModal from '../../components/adminSchools/projectLinkModal';
import AdminProcessScopeFilter from '../../components/adminProcessScopeFilter';
import CustomFilterSelect from '../../shared/customFilterSelect';
import {
  ADMIN_SCOPE_VALUES,
  buildAdminPathWithScope,
  buildScopedSchools,
  normalizeAdminScope
} from '../../utils/adminScope';
import { ENTITY_TYPE_FILTER_OPTIONS, matchesEntityType } from '../../utils/entityType';
import { getMultiFilterLabel, normalizeMultiSelection } from '../../utils/multiFilter';

const MEETING_TYPE_PRIORITY = {
  MEETING: 0,
  EVENT: 1
};

const SCHOOL_TYPE_OPTIONS = [
  { name: 'INSTITUCION EDUCATIVA', id: 'IE' },
  { name: 'DIPLOMADO', id: 'DIPLOMADO' },
  { name: 'JAL', id: 'JAL' },
  { name: 'JAC', id: 'JAC' },
  { name: 'ORGANIZACIONES SOCIALES', id: 'OS' },
  { name: 'COLECTIVO', id: 'COLECTIVO' }
];

const GROUP_TYPE_OPTIONS = [
  { label: 'Docentes', value: 'DOCENTES' },
  { label: 'Estudiantes', value: 'ESTUDIANTES' },
  { label: 'Comunidad', value: 'COMUNIDAD' }
];

const COP_PHASES = [
  { id: 'solicitud', label: 'Solicitud' },
  { id: 'planeacion', label: 'Planeacion' },
  { id: 'desarrollo', label: 'Desarrollo' },
  { id: 'cierre', label: 'Cierre' }
];

const COP_MEETING_ORDERS = [1, 2, 3, 4];

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function sortByText(values, getValue) {
  return [...values].sort((left, right) =>
    String(getValue(left) || '').localeCompare(String(getValue(right) || ''), 'es', {
      numeric: true,
      sensitivity: 'base'
    })
  );
}

function getUserDisplayName(user) {
  return String(user?.full_name ?? user?.fullName ?? user?.name ?? '').trim();
}

function getGroupTypeText(group) {
  const ageRange = String(group?.age_range ?? group?.ageRange ?? '').trim();
  if (ageRange) return ageRange;

  const legacyDescription = String(group?.description ?? '').trim();
  const normalizedLegacyDescription = normalizeText(legacyDescription);
  return ['docente', 'comunidad', 'estudiante'].some(type =>
    normalizedLegacyDescription.includes(type)
  )
    ? legacyDescription
    : '';
}

function getSchoolDisplayName(school) {
  return String(
    school?.name ?? school?.address ?? school?.code ?? 'Proceso Formativo'
  ).trim();
}

function getSchoolKey(school) {
  return String(school?.id ?? school?.code ?? getSchoolDisplayName(school));
}

function getMediatorKey(mediatorId, mediatorName) {
  if (mediatorId) {
    return `user-${mediatorId}`;
  }

  const normalizedName = normalizeText(mediatorName).replace(/[^a-z0-9]+/g, '-');
  return `name-${normalizedName || 'sin-mediador'}`;
}

function getSchoolMediator(school) {
  const schoolUser = school?.user;
  const groupUsers = Array.isArray(school?.groups)
    ? school.groups.map(group => group?.user).filter(Boolean)
    : [];
  const mediatorUser =
    (schoolUser && (schoolUser?.id || getUserDisplayName(schoolUser)) ? schoolUser : null) ||
    groupUsers[0] ||
    null;
  const mediatorId =
    mediatorUser?.id ??
    school?.userId ??
    groupUsers[0]?.id ??
    school?.groups?.find(group => group?.userId)?.userId ??
    '';
  const mediatorName = getUserDisplayName(mediatorUser) || 'Sin mediador';

  return {
    id: mediatorId ? String(mediatorId) : '',
    key: getMediatorKey(mediatorId, mediatorName),
    name: mediatorName
  };
}

function getSchoolMediatorName(school) {
  return getSchoolMediator(school).name;
}

function sortMeetings(meetings) {
  return sortByText(meetings, meeting => {
    const priority = MEETING_TYPE_PRIORITY[String(meeting?.type || '').trim().toUpperCase()] ?? 99;
    const order = Number(meeting?.order ?? 0);
    return `${priority.toString().padStart(2, '0')}-${order
      .toString()
      .padStart(3, '0')}`;
  });
}

function buildMeetingLabel(meeting) {
  const type = String(meeting?.type || '').trim().toUpperCase();
  const order = Number(meeting?.order);
  const safeOrder = Number.isInteger(order) && order > 0 ? order : '';

  if (type === 'EVENT') {
    return `Montar evento ${safeOrder}`.trim();
  }

  return `Montar encuentro ${safeOrder}`.trim();
}

function buildMeetingPath(meetingId) {
  const searchParams = new URLSearchParams({
    encuentro: String(meetingId || '')
  });

  return `/escuelas/grupo/lista_de_encuentros/encuentro?${searchParams.toString()}`;
}

function buildWorkspacePath(scope, mediatorKey = '', expandedSchoolKeys = []) {
  const path = buildAdminPathWithScope(
    '/administrador/menu/montar-evidencias',
    scope
  );

  if (!mediatorKey) {
    return path;
  }

  const [pathname, search = ''] = String(path || '').split('?');
  const searchParams = new URLSearchParams(search);
  if (mediatorKey) {
    searchParams.set('mediator', mediatorKey);
  }
  const normalizedExpandedSchoolKeys = Array.from(
    new Set((Array.isArray(expandedSchoolKeys) ? expandedSchoolKeys : []).filter(Boolean))
  );
  if (normalizedExpandedSchoolKeys.length > 0) {
    searchParams.set('expanded', normalizedExpandedSchoolKeys.join(','));
  }

  return `${pathname}?${searchParams.toString()}`;
}

function parseExpandedSchoolKeys(value) {
  return Array.from(
    new Set(
      String(value || '')
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
    )
  );
}

function buildSchoolSearchText(school) {
  const groups = Array.isArray(school?.groups) ? school.groups : [];

  return [
    school?.code,
    getSchoolDisplayName(school),
    getSchoolMediatorName(school),
    school?.municipality?.name,
    ...groups.map(group => group?.code),
    ...groups.map(group => group?.name),
    ...groups.map(getGroupTypeText)
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function buildMediatorSearchText(mediator) {
  return [
    mediator?.name,
    ...(Array.isArray(mediator?.municipalities) ? mediator.municipalities : []),
    ...(Array.isArray(mediator?.schools)
      ? mediator.schools.flatMap(school => [
          school?.code,
          getSchoolDisplayName(school),
          school?.municipality?.name
        ])
      : [])
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function AdminEvidenceWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const adminScope = normalizeAdminScope(searchParams.get('scope'));
  const isCopScope = adminScope === ADMIN_SCOPE_VALUES.COP;
  const selectedMediatorKey = String(searchParams.get('mediator') || '').trim();
  const expandedSchoolKeys = useMemo(
    () => parseExpandedSchoolKeys(searchParams.get('expanded')),
    [searchParams]
  );
  const workspacePath = buildWorkspacePath(
    adminScope,
    selectedMediatorKey,
    expandedSchoolKeys
  );

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [municipalities, setMunicipalities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [meetingsByGroup, setMeetingsByGroup] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([]);
  const [openAddGroupModal, setOpenAddGroupModal] = useState(false);
  const [selectedSchoolForGroup, setSelectedSchoolForGroup] = useState(null);
  const [selectedGroupForProjectLink, setSelectedGroupForProjectLink] = useState(null);
  const [selectedGroupType, setSelectedGroupType] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [loadingAdvisorMeetingKey, setLoadingAdvisorMeetingKey] = useState('');
  const loadingMeetingGroupIdsRef = useRef(new Set());
  const mediatorBadgeLabel = isCopScope
    ? 'Mediador COP'
    : adminScope === ADMIN_SCOPE_VALUES.NDN
      ? 'Mediador NDN'
      : 'Mediador';
  const processSingular = isCopScope ? 'asesoria' : 'proceso';
  const processPlural = isCopScope ? 'asesorias' : 'procesos';
  const detailCountLabel = isCopScope ? 'sesiones' : 'grupos';
  const totalAdvisorSessionsBySchool = COP_PHASES.length * COP_MEETING_ORDERS.length;

  function mapUsersResponse(list) {
    if (!Array.isArray(list)) return [];

    return list
      .filter(user => Boolean(user?.id))
      .map(user => ({
        id: user.id,
        name: String(user?.full_name ?? user?.username ?? '').trim(),
        roleName: user?.role?.name,
        roleId: user?.role?.id,
        active: user?.active === undefined ? true : Boolean(user.active)
      }))
      .filter(user => Boolean(user.name));
  }

  async function loadCreateDependencies() {
    const [municipalitiesResponse, usersResponse] = await Promise.all([
      getMunicipalities(),
      getUsers({ noCache: true })
    ]);

    setMunicipalities(
      Array.isArray(municipalitiesResponse) ? municipalitiesResponse : []
    );
    setUsers(mapUsersResponse(usersResponse));
  }

  async function loadPageData(shouldCancel) {
    setIsLoading(true);

    const schoolsResponse = await getSchools();

    if (typeof shouldCancel === 'function' && shouldCancel()) {
      return;
    }

    const scopedSchools = sortByText(
      buildScopedSchools(Array.isArray(schoolsResponse) ? schoolsResponse : [], adminScope),
      school => school?.code || school?.name || school?.address
    ).map(school => ({
      ...school,
      groups: sortByText(
        Array.isArray(school?.groups) ? school.groups : [],
        group => group?.code || group?.name
      )
    }));

    setSchools(scopedSchools);
    setIsLoading(false);
  }

  useEffect(() => {
    let cancelled = false;

    async function loadInitialData() {
      await loadPageData(() => cancelled);
    }

    loadInitialData();

    return () => {
      cancelled = true;
    };
  }, [adminScope]);

  const mediators = useMemo(() => {
    const mediatorMap = new Map();

    schools.forEach(school => {
      const mediator = getSchoolMediator(school);
      const currentMediator = mediatorMap.get(mediator.key) || {
        ...mediator,
        schools: [],
        totalGroups: 0,
        municipalities: []
      };

      currentMediator.schools.push(school);
      currentMediator.totalGroups += isCopScope
        ? totalAdvisorSessionsBySchool
        : Array.isArray(school?.groups)
          ? school.groups.length
          : 0;

      if (school?.municipality?.name) {
        currentMediator.municipalities = [
          ...currentMediator.municipalities,
          school.municipality.name
        ];
      }

      mediatorMap.set(mediator.key, currentMediator);
    });

    return sortByText(
      Array.from(mediatorMap.values()).map(mediator => ({
        ...mediator,
        schools: sortByText(
          Array.isArray(mediator.schools) ? mediator.schools : [],
          school => school?.code || school?.name || school?.address
        ),
        municipalities: sortByText(
          Array.from(new Set(mediator.municipalities)),
          municipality => municipality
        )
      })),
      mediator => mediator.name
    );
  }, [schools, isCopScope, totalAdvisorSessionsBySchool]);

  const hasSchools = schools.length > 0;
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const selectedMediator =
    mediators.find(mediator => mediator.key === selectedMediatorKey) || null;
  const selectedMediatorSchools = selectedMediator?.schools || [];

  const filteredMediators = useMemo(() => {
    if (selectedMediator) return [];

    return mediators.filter(mediator => {
      const matchesSearch = !normalizedSearchTerm ||
        buildMediatorSearchText(mediator).includes(normalizedSearchTerm);
      const matchesType = selectedEntityTypes.length === 0 ||
        mediator.schools.some(school => selectedEntityTypes.some(type => matchesEntityType(school, type)));
      return matchesSearch && matchesType;
    });
  }, [mediators, normalizedSearchTerm, selectedEntityTypes, selectedMediator]);

  const filteredSchools = useMemo(() => {
    if (!selectedMediator) return [];

    return selectedMediatorSchools.filter(school =>
      (!normalizedSearchTerm || buildSchoolSearchText(school).includes(normalizedSearchTerm)) &&
      (selectedEntityTypes.length === 0 || selectedEntityTypes.some(type => matchesEntityType(school, type)))
    );
  }, [normalizedSearchTerm, selectedEntityTypes, selectedMediator, selectedMediatorSchools]);

  const filteredGroups = useMemo(
    () =>
      filteredSchools.reduce(
        (total, school) =>
          total +
          (isCopScope
            ? totalAdvisorSessionsBySchool
            : Array.isArray(school?.groups)
              ? school.groups.length
              : 0),
        0
      ),
    [filteredSchools, isCopScope, totalAdvisorSessionsBySchool]
  );

  const visibleProcessesByMediator = useMemo(
    () =>
      filteredMediators.reduce(
        (total, mediator) =>
          total + (Array.isArray(mediator?.schools) ? mediator.schools.length : 0),
        0
      ),
    [filteredMediators]
  );

  const visibleGroupsByMediator = useMemo(
    () =>
      filteredMediators.reduce(
        (total, mediator) => total + Number(mediator?.totalGroups || 0),
        0
      ),
    [filteredMediators]
  );

  const hasVisibleSchools = filteredSchools.length > 0;
  const hasVisibleMediators = filteredMediators.length > 0;
  const isMediatorSelected = Boolean(selectedMediator);
  const expandedGroupIds = useMemo(
    () =>
      isCopScope
        ? []
        : (isMediatorSelected ? selectedMediatorSchools : [])
            .filter(school => expandedSchoolKeys.includes(getSchoolKey(school)))
            .flatMap(school => (Array.isArray(school?.groups) ? school.groups : []))
            .map(group => Number(group?.id))
            .filter(groupId => Number.isInteger(groupId) && groupId > 0),
    [expandedSchoolKeys, isMediatorSelected, isCopScope, selectedMediatorSchools]
  );

  function updateWorkspaceSearchParams({
    mediatorKey = selectedMediatorKey,
    expandedKeys = expandedSchoolKeys
  } = {}) {
    const nextSearchParams = new URLSearchParams();

    if (adminScope) {
      nextSearchParams.set('scope', adminScope);
    }

    if (mediatorKey) {
      nextSearchParams.set('mediator', mediatorKey);
    }

    const normalizedExpandedKeys = Array.from(
      new Set((Array.isArray(expandedKeys) ? expandedKeys : []).filter(Boolean))
    );
    if (normalizedExpandedKeys.length > 0) {
      nextSearchParams.set('expanded', normalizedExpandedKeys.join(','));
    }

    setSearchParams(nextSearchParams, { replace: true });
  }

  function toggleSchoolDetails(schoolKey) {
    const nextExpandedKeys = expandedSchoolKeys.includes(schoolKey)
      ? expandedSchoolKeys.filter(currentKey => currentKey !== schoolKey)
      : [...expandedSchoolKeys, schoolKey];

    updateWorkspaceSearchParams({
      expandedKeys: nextExpandedKeys
    });
  }

  function handleSelectMediator(mediatorKey) {
    setSearchTerm('');
    updateWorkspaceSearchParams({
      mediatorKey,
      expandedKeys: []
    });
  }

  function handleClearMediatorSelection() {
    setSearchTerm('');
    updateWorkspaceSearchParams({
      mediatorKey: '',
      expandedKeys: []
    });
  }

  function handleExpandVisibleSchools() {
    updateWorkspaceSearchParams({
      expandedKeys: filteredSchools.map(getSchoolKey)
    });
  }

  function handleCollapseVisibleSchools() {
    updateWorkspaceSearchParams({
      expandedKeys: []
    });
  }

  useEffect(() => {
    let cancelled = false;

    async function loadExpandedMeetings() {
      const pendingGroupIds = expandedGroupIds.filter(groupId => {
        const normalizedGroupId = String(groupId);
        return (
          meetingsByGroup[normalizedGroupId] === undefined &&
          !loadingMeetingGroupIdsRef.current.has(normalizedGroupId)
        );
      });

      if (pendingGroupIds.length === 0) {
        return;
      }

      pendingGroupIds.forEach(groupId => {
        loadingMeetingGroupIdsRef.current.add(String(groupId));
      });

      try {
        const meetingsEntries = await Promise.all(
          pendingGroupIds.map(async groupId => {
            const response = await getMeetingsByGroup(groupId);
            return [
              String(groupId),
              sortMeetings(Array.isArray(response) ? response : [])
            ];
          })
        );

        if (cancelled) {
          return;
        }

        setMeetingsByGroup(currentMeetings => ({
          ...currentMeetings,
          ...Object.fromEntries(meetingsEntries)
        }));
      } finally {
        pendingGroupIds.forEach(groupId => {
          loadingMeetingGroupIdsRef.current.delete(String(groupId));
        });
      }
    }

    loadExpandedMeetings();

    return () => {
      cancelled = true;
    };
  }, [expandedGroupIds, meetingsByGroup]);

  async function handleOpenCreateSchool() {
    if (isCopScope) return;

    await loadCreateDependencies();
    setOpenCreateModal(true);
  }

  function handleOpenAddGroupModal(school) {
    setSelectedSchoolForGroup(school);
    setSelectedGroupType('');
    setOpenAddGroupModal(true);
  }

  function handleOpenProjectLinkModal(school, group) {
    if (!group?.id) return;

    setSelectedGroupForProjectLink({
      school,
      group
    });
  }

  function handleCloseProjectLinkModal() {
    setSelectedGroupForProjectLink(null);
  }

  function handleProjectLinkSaved(updatedGroup) {
    const updatedGroupId = Number(updatedGroup?.id);
    if (!updatedGroupId) return;

    setSchools(currentSchools =>
      currentSchools.map(school => ({
        ...school,
        groups: Array.isArray(school?.groups)
          ? school.groups.map(group =>
              Number(group?.id) === updatedGroupId
                ? {
                    ...group,
                    description: updatedGroup.description
                  }
                : group
            )
          : school?.groups
      }))
    );
  }

  function handleCloseAddGroupModal() {
    if (isCreatingGroup) return;
    setOpenAddGroupModal(false);
    setSelectedSchoolForGroup(null);
    setSelectedGroupType('');
  }

  async function handleCreateGroup() {
    if (!selectedSchoolForGroup?.id) return;

    if (!selectedGroupType) {
      toast('Selecciona un tipo de grupo', { type: 'warning' });
      return;
    }

    setIsCreatingGroup(true);
    const response = await createSchoolGroup({
      schoolId: Number(selectedSchoolForGroup.id),
      groupType: selectedGroupType
    });
    setIsCreatingGroup(false);

    if (!response?.success) {
      toast('Error creando grupo', { type: 'error' });
      return;
    }

    toast('Grupo creado exitosamente', { type: 'success' });
    updateWorkspaceSearchParams({
      expandedKeys: Array.from(
        new Set([...expandedSchoolKeys, getSchoolKey(selectedSchoolForGroup)])
      )
    });
    handleCloseAddGroupModal();
    await loadPageData();
  }

  async function handleOpenAdvisorMeeting(school, phase, order) {
    if (!isCopScope || !school?.id || !phase || !order || loadingAdvisorMeetingKey) {
      return;
    }

    const loadingKey = `${school.id}-${phase}-${order}`;
    setLoadingAdvisorMeetingKey(loadingKey);

    try {
      const meeting = await getOrCreateAdvisorPhaseMeeting({
        schoolId: Number(school.id),
        phase,
        order: Number(order),
        userId: Number(school?.userId ?? school?.user_id ?? school?.user?.id)
      });
      const meetingGroupId = meeting?.meetingGroupId ?? meeting?.id;

      if (!meetingGroupId) {
        return;
      }

      const params = new URLSearchParams({
        encuentro: String(meetingGroupId),
        phase,
        order: String(order),
        origin: 'admin-evidence-workspace',
        returnTo: workspacePath
      });

      navigate(`/escuelas/grupo/lista_de_encuentros/encuentro?${params.toString()}`, {
        state: {
          backTo: workspacePath,
          school,
          advisorPhase: phase,
          advisorMeetingOrder: Number(order)
        }
      });
    } finally {
      setLoadingAdvisorMeetingKey('');
    }
  }

  async function handleCreateSchool(created) {
    if (!created) return;

    toast('Proceso Formativo creado exitosamente!', {
      type: 'success'
    });
    setOpenCreateModal(false);
    await loadPageData();
  }

  return (
    <section className='admin-evidence-workspace'>
      {openCreateModal && !isCopScope && (
          <ModalContainer>
          <CreateSchool
            municipalities={municipalities}
            schoolType={SCHOOL_TYPE_OPTIONS}
            setOpenModal={setOpenCreateModal}
            users={users}
            created={handleCreateSchool}
            defaultProgramScope={
              adminScope === ADMIN_SCOPE_VALUES.NDN
                ? 'NDN'
                : adminScope === ADMIN_SCOPE_VALUES.DIPLOM
                  ? 'DIPLOM'
                  : 'PF'
            }
          />
        </ModalContainer>
      )}
      {openAddGroupModal && selectedSchoolForGroup && (
        <ModalContainer>
          <section className='school-group-modal'>
            <h3 className='school-group-modal__title'>Agregar grupo</h3>
            <p className='school-group-modal__text'>
              Proceso formativo: {getSchoolDisplayName(selectedSchoolForGroup)}
            </p>
            <label
              className='school-group-modal__label'
              htmlFor='admin-evidence-group-type'
            >
              Tipo de grupo
            </label>
            <select
              id='admin-evidence-group-type'
              className='school-group-modal__select'
              value={selectedGroupType}
              onChange={event => setSelectedGroupType(event.target.value)}
              disabled={isCreatingGroup}
            >
              <option value=''>Selecciona una opcion</option>
              {GROUP_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className='school-group-modal__actions'>
              <button
                type='button'
                className='blue-site-button'
                onClick={handleCreateGroup}
                disabled={isCreatingGroup}
              >
                {isCreatingGroup ? 'CREANDO...' : 'CREAR'}
              </button>
              <button
                type='button'
                className='button-white-border-blue'
                onClick={handleCloseAddGroupModal}
                disabled={isCreatingGroup}
              >
                CANCELAR
              </button>
            </div>
          </section>
        </ModalContainer>
      )}
      {selectedGroupForProjectLink?.group && (
        <ModalContainer>
          <ProjectLinkModal
            group={selectedGroupForProjectLink.group}
            onClose={handleCloseProjectLinkModal}
            onSaved={handleProjectLinkSaved}
          />
        </ModalContainer>
      )}
      <div className='workspace-hero'>
        <div>
          <p className='workspace-hero__eyebrow'>
            {isCopScope ? 'Centro de Orientacion Pedagogica' : 'Nada de Nervios'}
          </p>
          <h1 className='workspace-hero__title'>Montar evidencias</h1>
          <p className='workspace-hero__description'>
            {isMediatorSelected
              ? `Administra ${processPlural}, ${detailCountLabel} y evidencias del mediador ${selectedMediator.name}.`
              : `Selecciona un ${mediatorBadgeLabel.toLowerCase()} para administrar sus ${processPlural} y evidencias.`}
          </p>
        </div>
        <div className='workspace-hero__stats'>
          {!isCopScope && (
            <button
              type='button'
              className='workspace-hero__action'
              onClick={handleOpenCreateSchool}
            >
              Crear proceso formativo
            </button>
          )}
          <div className='workspace-stat'>
            <span className='workspace-stat__value'>
              {isMediatorSelected ? filteredSchools.length : filteredMediators.length}
            </span>
            <span className='workspace-stat__label'>
              {isMediatorSelected ? processPlural : 'Mediadores'}
            </span>
          </div>
          <div className='workspace-stat'>
            <span className='workspace-stat__value'>
              {isMediatorSelected ? filteredGroups : visibleProcessesByMediator}
            </span>
            <span className='workspace-stat__label'>
              {isMediatorSelected ? detailCountLabel : processPlural}
            </span>
          </div>
        </div>
      </div>

      <div className='workspace-toolbar'>
        <div className='workspace-search'>
          <AdminProcessScopeFilter
            allowedScopes={['general', 'pf', 'diplom', 'ndn']}
            variant='workspace'
            onScopeChange={() => {
              setSearchTerm('');
              setSelectedEntityTypes([]);
            }}
          />
          <label className='workspace-search__label' htmlFor='admin-evidence-search'>
            {isMediatorSelected ? `Buscar ${processSingular}` : `Buscar ${mediatorBadgeLabel}`}
          </label>
          <input
            id='admin-evidence-search'
            className='workspace-search__input'
            type='text'
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            placeholder={
              isMediatorSelected
                ? `Busca por codigo, nombre${isCopScope ? ' o comuna' : ', comuna o grupo'}`
                : `Busca por nombre del mediador, ${processSingular} o comuna`
            }
          />
          {!isCopScope && (
            <CustomFilterSelect
              optionsList={ENTITY_TYPE_FILTER_OPTIONS}
              placeholder='Tipo de entidad'
              valueEdit={getMultiFilterLabel(ENTITY_TYPE_FILTER_OPTIONS, selectedEntityTypes)}
              addInfo={(_, values) => setSelectedEntityTypes(normalizeMultiSelection(values))}
              multiple
              selectedValues={selectedEntityTypes}
            />
          )}
        </div>

        <div className='workspace-toolbar__summary'>
          <span className='workspace-toolbar__summary-eyebrow'>
            {isMediatorSelected ? 'Mediador seleccionado' : 'Resumen visible'}
          </span>
          <strong className='workspace-toolbar__summary-title'>
            {isMediatorSelected
              ? selectedMediator.name
              : `${filteredMediators.length} mediador${
                  filteredMediators.length === 1 ? '' : 'es'
                }`}
          </strong>
          <span className='workspace-toolbar__summary-meta'>
            {isMediatorSelected ? (
              <>
                {filteredSchools.length} {processSingular}
                {filteredSchools.length === 1 ? '' : 's'} | {filteredGroups}{' '}
                {detailCountLabel}
              </>
            ) : (
              <>
                {visibleProcessesByMediator} {processSingular}
                {visibleProcessesByMediator === 1 ? '' : 's'} | {visibleGroupsByMediator}{' '}
                {detailCountLabel}
              </>
            )}
          </span>
        </div>

        <div className='workspace-toolbar__actions'>
          {isMediatorSelected && (
            <>
              <button
                type='button'
                className='workspace-toolbar__button workspace-toolbar__button--ghost workspace-toolbar__button--back'
                onClick={handleClearMediatorSelection}
              >
                Volver a mediadores
              </button>
              <button
                type='button'
                className='workspace-toolbar__button'
                onClick={handleExpandVisibleSchools}
                disabled={!hasVisibleSchools}
              >
                Expandir resultados
              </button>
              <button
                type='button'
                className='workspace-toolbar__button workspace-toolbar__button--ghost'
                onClick={handleCollapseVisibleSchools}
                disabled={expandedSchoolKeys.length === 0}
              >
                Contraer resultados
              </button>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className='workspace-state'>
          Cargando {processPlural}...
        </div>
      ) : !hasSchools ? (
        <div className='workspace-state'>
          No hay {processPlural} disponibles para montar evidencias.
        </div>
      ) : isMediatorSelected ? (
        !hasVisibleSchools ? (
          <div className='workspace-state'>
            No encontramos {processPlural} para ese mediador con ese criterio de busqueda.
          </div>
        ) : (
        <div className='workspace-list'>
          {filteredSchools.map(school => {
            const schoolName = getSchoolDisplayName(school);
            const schoolCode = String(school?.code || '').trim() || 'Sin codigo';
            const mediatorName = getSchoolMediatorName(school);
            const municipalityName =
              school?.municipality?.name || 'Sin comuna asignada';
            const schoolState =
              school?.active === false ? 'Inactivo' : 'Activo';
            const schoolKey = getSchoolKey(school);
            const isExpanded = expandedSchoolKeys.includes(schoolKey);
            const schoolGroups = Array.isArray(school?.groups) ? school.groups : [];
            const schoolDetailCount = isCopScope
              ? totalAdvisorSessionsBySchool
              : schoolGroups.length;

            return (
              <article className='workspace-card' key={schoolKey}>
                <div className='workspace-card__header'>
                  <div className='workspace-card__identity'>
                    <div className='workspace-card__badges'>
                      <span className='workspace-badge'>{schoolCode}</span>
                      <span className='workspace-badge workspace-badge--muted'>
                        {schoolState}
                      </span>
                      <span className='workspace-badge workspace-badge--muted'>
                        {schoolDetailCount} {detailCountLabel}
                      </span>
                    </div>
                    <h2 className='workspace-card__title'>{schoolName}</h2>
                    <p className='workspace-card__meta'>
                      Comuna: {municipalityName} | Mediador: {mediatorName}
                    </p>
                  </div>

                  <div className='workspace-card__actions'>
                    {!isCopScope && (
                      <>
                        <Link
                          className='workspace-action workspace-action--primary'
                          to={`/socializacion/${school?.id}?number=1`}
                          state={{ backTo: workspacePath }}
                        >
                          Montar socialización 1
                        </Link>
                        <Link
                          className='workspace-action workspace-action--primary'
                          to={`/socializacion/${school?.id}?number=2`}
                          state={{ backTo: workspacePath }}
                        >
                          Montar socialización 2
                        </Link>
                        <button
                          type='button'
                          className='workspace-action workspace-action--secondary'
                          onClick={() => handleOpenAddGroupModal(school)}
                        >
                          Agregar grupo
                        </button>
                      </>
                    )}
                    <button
                      type='button'
                      className={`workspace-action ${isCopScope ? 'workspace-action--primary' : 'workspace-action--secondary'}`}
                      aria-expanded={isExpanded}
                      onClick={() => toggleSchoolDetails(schoolKey)}
                    >
                      {isExpanded ? 'Ocultar detalle' : 'Ver detalle'}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  isCopScope ? (
                    <div className='workspace-groups workspace-groups--advisor'>
                      {COP_PHASES.map(phase => (
                        <section
                          className='workspace-group workspace-group--advisor'
                          key={`${school?.id}-${phase.id}`}
                        >
                          <div className='workspace-group__header'>
                            <div>
                              <h3 className='workspace-group__title'>
                                {phase.label}
                              </h3>
                              <p className='workspace-group__description'>
                                Sesiones registradas para la asesoria.
                              </p>
                            </div>
                          </div>

                          <div className='workspace-group__actions'>
                            {COP_MEETING_ORDERS.map(order => {
                              const loadingKey = `${school.id}-${phase.id}-${order}`;
                              const isLoading = loadingAdvisorMeetingKey === loadingKey;

                              return (
                                <button
                                  key={`${phase.id}-${order}`}
                                  type='button'
                                  className='workspace-action'
                                  onClick={() =>
                                    handleOpenAdvisorMeeting(school, phase.id, order)
                                  }
                                  disabled={Boolean(loadingAdvisorMeetingKey)}
                                >
                                  {isLoading ? 'Abriendo...' : `Encuentro ${order}`}
                                </button>
                              );
                            })}
                          </div>
                        </section>
                      ))}
                    </div>
                  ) : (
                  <div className='workspace-groups'>
                    {schoolGroups.length > 0 ? (
                      schoolGroups.map(group => {
                        const groupDescription =
                          getGroupTypeText(group) ||
                          group?.formativeProcessType ||
                          'Grupo';
                        const groupCode =
                          String(group?.code || group?.name || 'Sin grupo').trim();
                        const groupMeetings =
                          meetingsByGroup[String(group?.id)] || [];
                        const groupState = {
                          school,
                          group,
                          backTo: workspacePath
                        };

                        return (
                          <section
                            className='workspace-group'
                            key={`${school?.id}-${group?.id}`}
                          >
                            <div className='workspace-group__header'>
                              <div>
                                <h3 className='workspace-group__title'>
                                  {groupCode}
                                </h3>
                                <p className='workspace-group__description'>
                                  {groupDescription}
                                </p>
                              </div>

                              <Link
                                className='workspace-action workspace-action--secondary'
                                to={`/escuelas/grupo/${group?.id}/school/${school?.id}`}
                                state={groupState}
                              >
                                Caracterizar beneficiario
                              </Link>
                            </div>

                            <div className='workspace-group__actions'>
                              <button
                                type='button'
                                className='workspace-action workspace-action--secondary'
                                onClick={() => handleOpenProjectLinkModal(school, group)}
                              >
                                Link proyecto
                              </button>
                              {groupMeetings.length > 0 &&
                                groupMeetings.map(meeting => (
                                  <Link
                                    key={meeting?.id}
                                    className='workspace-action'
                                    to={buildMeetingPath(meeting?.id)}
                                    state={groupState}
                                  >
                                    {buildMeetingLabel(meeting)}
                                  </Link>
                                ))}
                            </div>
                            {groupMeetings.length === 0 && (
                              <p className='workspace-group__empty'>
                                No hay encuentros configurados para este grupo.
                              </p>
                            )}
                          </section>
                        );
                      })
                    ) : (
                      <div className='workspace-group workspace-group--empty'>
                        <p className='workspace-group__empty'>
                          Este proceso formativo no tiene grupos disponibles en este alcance.
                        </p>
                      </div>
                    )}
                  </div>
                  )
                )}
              </article>
            );
          })}
        </div>
        )
      ) : !hasVisibleMediators ? (
        <div className='workspace-state'>
          No encontramos mediadores con ese criterio de busqueda.
        </div>
      ) : (
        <div className='workspace-mediator-grid'>
          {filteredMediators.map(mediator => (
            <article className='workspace-mediator-card' key={mediator.key}>
              <div className='workspace-mediator-card__header'>
                <div className='workspace-mediator-card__identity'>
                  <div className='workspace-card__badges'>
                    <span className='workspace-badge'>{mediatorBadgeLabel}</span>
                    <span className='workspace-badge workspace-badge--muted'>
                      {mediator.schools.length} {processSingular}
                      {mediator.schools.length === 1 ? '' : 's'}
                    </span>
                    <span className='workspace-badge workspace-badge--muted'>
                      {mediator.totalGroups} {detailCountLabel}
                    </span>
                  </div>
                  <h2 className='workspace-mediator-card__title'>
                    {mediator.name}
                  </h2>
                  <p className='workspace-mediator-card__meta'>
                    {mediator.municipalities.length > 0
                      ? `Comunas: ${mediator.municipalities.join(', ')}`
                      : 'Sin comunas asignadas'}
                  </p>
                </div>

                <button
                  type='button'
                  className='workspace-action workspace-action--primary'
                  onClick={() => handleSelectMediator(mediator.key)}
                >
                  Ver {processPlural}
                </button>
              </div>

              <div className='workspace-mediator-card__processes'>
                {mediator.schools.slice(0, 4).map(school => (
                  <span
                    className='workspace-chip'
                    key={`${mediator.key}-${getSchoolKey(school)}`}
                  >
                    {String(school?.code || '').trim() || getSchoolDisplayName(school)}
                  </span>
                ))}
                {mediator.schools.length > 4 && (
                  <span className='workspace-chip workspace-chip--muted'>
                    +{mediator.schools.length - 4} mas
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default AdminEvidenceWorkspace;
